import { DEFAULT_QUERY_RETRY } from '../../lib/queryRetry';
import { ApiError } from '../../utils/httpError';

/**
 * The `code` the titles service puts on its 400 when a client-name search
 * matches more clients than it will resolve. Mirrors
 * `CLIENT_SEARCH_TOO_BROAD_CODE` in the titles app (no package is imported by
 * both sides, so keep the two in step).
 */
export const CLIENT_SEARCH_TOO_BROAD_CODE = 'CLIENT_SEARCH_TOO_BROAD';

export const isClientSearchTooBroad = (error: unknown) =>
  error instanceof ApiError &&
  error.status === 400 &&
  error.serverCode === CLIENT_SEARCH_TOO_BROAD_CODE;

/**
 * What the Titles page's query stores for a refused too-broad search. A
 * query that ends in an error holds no data, so TanStack Query always treats
 * it as stale and fetches it again whenever it is revisited, however long
 * `staleTime` is. The refusal is deterministic, so it is kept as a result
 * instead and gets the same `staleTime` as rows.
 */
export const TITLES_TOO_BROAD = { tooBroad: true } as const;

export type TitlesTooBroad = typeof TITLES_TOO_BROAD;

export const isTitlesTooBroad = (value: unknown): value is TitlesTooBroad =>
  typeof value === 'object' &&
  value !== null &&
  'tooBroad' in value &&
  value.tooBroad === true;

/**
 * Runs a titles request and turns the too-broad refusal into
 * {@link TITLES_TOO_BROAD}. Every other failure, the 503 lookup failure
 * included, still throws, so it stays an error that is retried and refetched.
 */
export const tooBroadAsResult = async <T>(
  request: () => Promise<T>,
): Promise<T | TitlesTooBroad> => {
  try {
    return await request();
  } catch (error) {
    if (isClientSearchTooBroad(error)) return TITLES_TOO_BROAD;

    throw error;
  }
};

/**
 * The `code` on the titles service's 503 when it cannot look the client names
 * up. Mirrors `CLIENT_LOOKUP_FAILED_CODE` in the titles app.
 */
export const CLIENT_LOOKUP_FAILED_CODE = 'CLIENT_LOOKUP_FAILED';

/** Only the lookup's own 503: one from API Gateway or Lambda has no `code`. */
export const isClientLookupFailed = (error: unknown) =>
  error instanceof ApiError &&
  error.status === 503 &&
  error.serverCode === CLIENT_LOOKUP_FAILED_CODE;

/**
 * Retry rule for the titles list. The too-broad refusal is deterministic, so a
 * retry only repeats a request that is certain to fail again. The lookup
 * failure is often the backend's 10s workspaces timeout, so a silent retry
 * doubles the wait; the page offers its own Retry button instead. Everything
 * else keeps the app's default retry count (`DEFAULT_QUERY_RETRY`).
 */
export const shouldRetryTitlesQuery = (failureCount: number, error: Error) =>
  !isClientSearchTooBroad(error) &&
  !isClientLookupFailed(error) &&
  failureCount < DEFAULT_QUERY_RETRY;
